// @ts-nocheck
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from "axios";
import { progamersApi } from "@/config/axios.config.ts";

/**
 * Enhanced API client with better error handling, request cancellation, and standardized responses
 */
export class ApiClient {
  private api: AxiosInstance;
  private cancelTokens: Map<string, CancelTokenSource>;

  constructor(api: AxiosInstance = progamersApi) {
    this.api = api;
    this.cancelTokens = new Map();
  }

  /**
   * Generate a unique request ID for tracking and cancellation
   */
  private generateRequestId(config: AxiosRequestConfig): string {
    const { method = 'GET', url = '', params = {}, data = {} } = config;
    return `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`;
  }

  /**
   * Cancel any ongoing request with the same ID
   */
  private cancelExistingRequest(requestId: string): void {
    const existingCancelToken = this.cancelTokens.get(requestId);
    if (existingCancelToken) {
      existingCancelToken.cancel(`Request ${requestId} was superseded`);
      this.cancelTokens.delete(requestId);
    }
  }

  /**
   * Make an API request with enhanced error handling and cancellation
   */
  private async request<T>(config: AxiosRequestConfig & { skipCancelToken?: boolean }): Promise<T> {
    const requestId = this.generateRequestId(config);

    const shouldSkipCancel = config.skipCancelToken === true;

    if (!shouldSkipCancel) {
      this.cancelExistingRequest(requestId);
    }

    const cancelTokenSource = axios.CancelToken.source();
    this.cancelTokens.set(requestId, cancelTokenSource);

    try {
      const response: AxiosResponse<T> = await this.api.request<T>({
        ...config,
        cancelToken: cancelTokenSource.token,
      });

      this.cancelTokens.delete(requestId);
      return response.data;
    } catch (error) {
      this.cancelTokens.delete(requestId);

      if (axios.isCancel(error)) {
        throw error;
      }

      const axiosError = error as AxiosError;

      const enhancedError = new Error(
        axiosError.response?.data?.message || axiosError.message || 'Unknown error'
      );

      (enhancedError as any).status = axiosError.response?.status;
      (enhancedError as any).data = axiosError.response?.data;
      (enhancedError as any).originalError = axiosError;

      throw enhancedError;
    }
  }


  /**
   * Cancel all ongoing requests
   */
  public cancelAll(): void {
    this.cancelTokens.forEach((source) => {
      source.cancel('Operation cancelled by user');
    });
    this.cancelTokens.clear();
  }

  /**
   * Make a GET request
   */
  public async get<T>(url: string, config?: Omit<AxiosRequestConfig, 'url' | 'method'>): Promise<T> {
    return this.request<T>({ ...config, url, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  public async post<T>(url: string, data?: any, config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>): Promise<T> {
    return this.request<T>({ ...config, url, method: 'POST', data });
  }

  /**
   * Make a PUT request
   */
  public async put<T>(url: string, data?: any, config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>): Promise<T> {
    return this.request<T>({ ...config, url, method: 'PUT', data });
  }

  /**
   * Make a DELETE request
   */
  public async delete<T>(url: string, config?: Omit<AxiosRequestConfig, 'url' | 'method'>): Promise<T> {
    return this.request<T>({ ...config, url, method: 'DELETE' });
  }

  /**
   * Make a PATCH request
   */
  public async patch<T>(url: string, data?: any, config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>): Promise<T> {
    return this.request<T>({ ...config, url, method: 'PATCH', data });
  }
}

// Create and export a default instance
const apiClient = new ApiClient();
export default apiClient;